import { useState } from "react";
import {
  addRecord,
  addSource,
  BLANK,
  D,
  removeRecord,
  reviseRecord,
  UNKNOWN,
  type Account,
  type Amount,
  type FormStatus,
  type Source,
  type YearFile,
} from "@ftr/tax-core";
import { Card } from "../components/ui.tsx";
import { usd } from "../lib/format.ts";
import { newId, nowIso } from "../lib/yearStore.ts";
import { useOrganizer } from "./context.tsx";
import { Editor, IssuesFor, personName, Row, StateBadge } from "./common.tsx";
import { AmountInput, AmountText, amountOk, Chips, DateInput, TextInput, YesNoField } from "./fields.tsx";
import { candidateFingerprint, type Form1099BCandidate, type FormCandidate } from "../import/parse1099.ts";
import { ImportedBanner, importHint, ImportPanel, type ImportedFile } from "./ImportPanel.tsx";

type Form1099B = Extract<Source, { kind: "form_1099b" }>;
type Statement = Extract<Source, { kind: "broker_statement" }>;

export const BROKERS = [
  "NinjaTrader Clearing (Tradovate / NinjaTrader)",
  "AMP Futures",
  "Optimus Futures",
  "Interactive Brokers",
  "Charles Schwab",
  "TradeStation",
  "tastytrade",
  "Ironbeam",
  "EdgeClear",
  "StoneX",
  "Wedbush",
  "Phillip Capital",
  "Dorman Trading",
  "Robinhood",
  "Webull",
  "E*TRADE",
];

export function FuturesStep() {
  const { yf, update, analysis } = useOrganizer();
  const accounts = yf.accounts.filter((a) => a.kind === "futures_broker" && a.deletedAt === null);
  const [adding, setAdding] = useState(false);

  return (
    <div className="steps-body">
      <Card title="Futures accounts in your name">
        <p className="card-intro">
          Add each account where you traded futures with your own money in 2026, including accounts you've since closed. Prop firm accounts go in the
          next step.
        </p>
        <div className="module-line">
          <StateBadge state={analysis.modules.section1256.state} />
          <span>Form 6781 figures</span>
        </div>
        {accounts.map((a) => (
          <AccountCard key={a.id} account={a} />
        ))}
        {adding ? (
          <AccountEditor onDone={() => setAdding(false)} />
        ) : (
          <button type="button" className="btn btn--outline" onClick={() => setAdding(true)}>
            + Add a futures account
          </button>
        )}
        <div className="form-section">
          <YesNoField
            legend="Is that every futures account you had in 2026, open or closed?"
            hint="If you only trade at prop firms, answer yes with no accounts listed."
            value={yf.household.allFuturesAccountsListed}
            onChange={(v) => update((y) => ({ ...y, household: { ...y.household, allFuturesAccountsListed: v } }))}
          />
        </div>
      </Card>
    </div>
  );
}

function OwnerChips({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { yf } = useOrganizer();
  if (yf.people.length < 2) return null;
  const options = [...yf.people.map((p) => ({ value: p.id, label: personName(yf, p.id) })), { value: "joint", label: "Joint" }];
  return <Chips legend="Whose account is it?" value={value} options={options} onChange={onChange} />;
}

function AccountEditor({ onDone, account }: { onDone: () => void; account?: Account }) {
  const { yf, update } = useOrganizer();
  const primary = yf.people.find((p) => p.role === "primary")!;
  const [label, setLabel] = useState(account?.label ?? "");
  const [ownerId, setOwner] = useState<string>(account?.ownerId ?? primary.id);
  const [status, setStatus] = useState<FormStatus>(account?.forms.form_1099b ?? null);
  const save = () => {
    update((y) =>
      account
        ? reviseRecord(y, "accounts", account.id, { label: label.trim(), ownerId, forms: { ...account.forms, form_1099b: status } })
        : addRecord(y, "accounts", { id: newId(), kind: "futures_broker", ownerId, firmId: null, label: label.trim(), coverageComplete: null, forms: { form_1099b: status } }),
    );
    onDone();
  };
  return (
    <Editor title={account ? "Edit account" : "New futures account"} onSave={save} onCancel={onDone} canSave={label.trim().length > 0}>
      <TextInput label="Broker and account" value={label} onChange={setLabel} placeholder="e.g. Tradovate ••4821" list="ftr-brokers" hint="Last four digits are enough. Don't enter a full account number." />
      <datalist id="ftr-brokers">
        {BROKERS.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>
      <OwnerChips value={ownerId} onChange={setOwner} />
      <FormStatusChips value={status} onChange={setStatus} form="1099-B" />
    </Editor>
  );
}

export function FormStatusChips({ value, onChange, form }: { value: FormStatus; onChange: (v: FormStatus) => void; form: string }) {
  return (
    <Chips
      legend={`Has the ${form} for 2026 arrived?`}
      value={value}
      options={[
        { value: "received", label: "Yes" },
        { value: "awaiting", label: "Not yet" },
        { value: "not_issued", label: "Won't be issued" },
      ]}
      onChange={onChange}
    />
  );
}

function AccountCard({ account }: { account: Account }) {
  const { yf, update, analysis } = useOrganizer();
  const [editing, setEditing] = useState<"account" | "form" | "statement" | null>(null);
  const [editForm, setEditForm] = useState<Form1099B | null>(null);
  const forms = yf.sources.filter((s): s is Form1099B => s.kind === "form_1099b" && s.accountId === account.id && s.deletedAt === null);
  const statement = yf.sources.find((s): s is Statement => s.kind === "broker_statement" && s.accountId === account.id && s.deletedAt === null);
  const basisDecision = yf.decisions.find((d) => d.kind === "section1256_basis" && d.accountId === account.id && d.deletedAt === null);
  const excluded = new Map(analysis.excludedSources.map((e) => [e.sourceId, e.reason]));
  const result = analysis.modules.section1256;
  const counted = result.state !== "not_applicable" ? (result.state === "blocked" ? result.partial : result.value)?.perAccount.find((p) => p.accountId === account.id) : undefined;
  const recon = analysis.reconciliation.futures.find((r) => r.accountId === account.id);

  const removeAccount = () => {
    if (!confirm(`Remove ${account.label} and its forms? You can't undo this.`)) return;
    update((y) => {
      let next = removeRecord(y, "accounts", account.id, nowIso());
      for (const s of y.sources) if (s.accountId === account.id && s.deletedAt === null) next = removeRecord(next, "sources", s.id, nowIso());
      return next;
    });
  };

  return (
    <div className="acct">
      <div className="acct-head">
        <div>
          <p className="acct-name">{account.label}</p>
          <p className="acct-meta">
            {yf.people.length > 1 && <>{personName(yf, account.ownerId)} · </>}
            {account.forms.form_1099b === "received" ? "1099-B received" : account.forms.form_1099b === "awaiting" ? "Waiting for the 1099-B" : account.forms.form_1099b === "not_issued" ? "No 1099-B" : "1099-B status not set"}
          </p>
        </div>
        <div className="acct-figure">
          {counted ? (
            <>
              <span className="acct-figure-label">{counted.basisFrom === "statement" ? "From statement" : counted.basisFrom === "no_activity" ? "No trading" : "Box 11"}</span>
              <span className="figure">{fmtUsd(counted.basis)}</span>
            </>
          ) : (
            <span className="muted">Not counted yet</span>
          )}
        </div>
      </div>

      {forms.map((f) => {
        const r = recon?.forms.find((x) => x.sourceId === f.id);
        const why = excluded.get(f.id);
        return (
          <Row
            key={f.id}
            actions={
              <>
                <button type="button" className="link-btn" onClick={() => { setEditForm(f); setEditing("form"); }}>Edit</button>
                <button type="button" className="link-btn" onClick={() => confirm("Remove this form?") && update((y) => removeRecord(y, "sources", f.id, nowIso()))}>Remove</button>
              </>
            }
          >
            <p className="row-title">
              1099-B{f.correctedAsPrinted ? " (corrected)" : ""} · {f.issuer || "issuer not entered"}
              {f.provenance === "imported" && <span className="pill pill--muted">from PDF</span>}
              {why && <span className={`pill pill--${why === "superseded" ? "muted" : "warn"}`}>{EXCLUDED[why] ?? why}</span>}
            </p>
            <p className="row-sub">
              {f.description} · Box 11 <AmountText a={f.box11} />
              {r?.difference && <span className="warn-text"> · boxes 8–10 differ by {fmtUsd(r.difference)}</span>}
            </p>
          </Row>
        );
      })}

      {statement && (
        <Row
          actions={
            <>
              <button type="button" className="link-btn" onClick={() => setEditing("statement")}>Edit</button>
              <button type="button" className="link-btn" onClick={() => update((y) => removeStatement(y, statement.id))}>Remove</button>
            </>
          }
        >
          <p className="row-title">Year-end statement</p>
          <p className="row-sub">
            Realized <AmountText a={statement.realizedPnl} /> · open at 2025 year-end <AmountText a={statement.unrealizedPriorYearEnd} /> · open at 2026 year-end{" "}
            <AmountText a={statement.unrealizedYearEnd} />
          </p>
        </Row>
      )}

      <IssuesFor ids={[account.id, ...forms.map((f) => f.id), ...(statement ? [statement.id] : [])]} exclude={["SCREENING_NEEDED"]} />

      {editing === "account" && <AccountEditor account={account} onDone={() => setEditing(null)} />}
      {editing === "form" && <Form1099BEditor account={account} existing={editForm} onDone={() => { setEditing(null); setEditForm(null); }} />}
      {editing === "statement" && <StatementEditor account={account} existing={statement ?? null} onDone={() => setEditing(null)} />}

      {editing === null && (
        <div className="acct-actions">
          <button type="button" className="btn btn--outline btn--sm" onClick={() => { setEditForm(null); setEditing("form"); }}>
            + Add 1099-B
          </button>
          {account.forms.form_1099b === "not_issued" && !forms.length && !statement && (
            <button type="button" className="btn btn--outline btn--sm" onClick={() => setEditing("statement")}>
              Use year-end statement
            </button>
          )}
          {account.forms.form_1099b === "not_issued" && !forms.length && !basisDecision && (
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => update((y) => addRecord(y, "decisions", { id: newId(), kind: "section1256_basis", accountId: account.id, basis: "no_activity", sourceId: null, sourceRevision: null }))}
            >
              No futures trading here
            </button>
          )}
          {basisDecision && basisDecision.kind === "section1256_basis" && basisDecision.basis === "no_activity" && (
            <button type="button" className="link-btn" onClick={() => update((y) => removeRecord(y, "decisions", basisDecision.id, nowIso()))}>
              Undo "no trading"
            </button>
          )}
          <button type="button" className="link-btn" onClick={() => setEditing("account")}>Edit account</button>
          <button type="button" className="link-btn link-btn--danger" onClick={removeAccount}>Remove account</button>
        </div>
      )}
    </div>
  );
}

const EXCLUDED: Record<string, string> = {
  superseded: "Replaced by a correction",
  voided: "Voided",
  replacement_pending: "Correction not confirmed",
  corrected_unlinked: "Not linked to its original",
  chain_unresolved: "Conflicting corrections",
  duplicate_unresolved: "Possible duplicate",
  wrong_year: "Different tax year",
  unassigned: "Not assigned",
};

export function fmtUsd(v: string): string {
  return usd(new D(v), { cents: true });
}

function removeStatement(y: YearFile, id: string): YearFile {
  let next = removeRecord(y, "sources", id, nowIso());
  for (const d of y.decisions) if (d.kind === "section1256_basis" && d.sourceId === id && d.deletedAt === null) next = removeRecord(next, "decisions", d.id, nowIso());
  return next;
}

type Relation = "corrects" | "separate" | "same";

function Form1099BEditor({ account, existing, onDone }: { account: Account; existing: Form1099B | null; onDone: () => void }) {
  const { yf, update, analysis } = useOrganizer();
  const [issuer, setIssuer] = useState(existing?.issuer ?? "");
  const [description, setDescription] = useState(existing?.description ?? "Regulated futures contracts");
  const [boxes, setBoxes] = useState<Record<"box4" | "box8" | "box9" | "box10" | "box11", Amount>>({
    box4: existing?.box4 ?? BLANK,
    box8: existing?.box8 ?? BLANK,
    box9: existing?.box9 ?? BLANK,
    box10: existing?.box10 ?? BLANK,
    box11: existing?.box11 ?? UNKNOWN,
  });
  const [corrected, setCorrected] = useState(existing?.correctedAsPrinted ?? false);
  const [relation, setRelation] = useState<Relation | null>(null);
  const [replaces, setReplaces] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<{ cand: Form1099BCandidate; file: ImportedFile } | null>(null);
  // Bumped when an import fills the boxes, so the amount inputs re-read their values.
  const [fillKey, setFillKey] = useState(0);

  const useImport = (c: FormCandidate, file: ImportedFile) => {
    if (c.kind !== "form_1099b") return;
    setIssuer(c.issuer.found ? c.issuer.value : "");
    setDescription(c.description.found ? c.description.value : "");
    setBoxes({
      box4: c.box4.found ? c.box4.value : BLANK,
      box8: c.box8.found ? c.box8.value : BLANK,
      box9: c.box9.found ? c.box9.value : BLANK,
      box10: c.box10.found ? c.box10.value : BLANK,
      box11: c.box11.found ? c.box11.value : UNKNOWN,
    });
    setCorrected(c.corrected.found ? c.corrected.value : false);
    setImported({ cand: c, file });
    setImporting(false);
    setFillKey((k) => k + 1);
  };
  const clearImport = () => {
    setIssuer("");
    setDescription("Regulated futures contracts");
    setBoxes({ box4: BLANK, box8: BLANK, box9: BLANK, box10: BLANK, box11: UNKNOWN });
    setCorrected(false);
    setImported(null);
    setFillKey((k) => k + 1);
  };
  const hint = (k: "issuer" | "box4" | "box8" | "box9" | "box10" | "box11") => (imported ? importHint(imported.cand[k]) : null);

  const excluded = new Set(analysis.excludedSources.filter((e) => e.reason === "superseded" || e.reason === "voided").map((e) => e.sourceId));
  const others = existing
    ? []
    : yf.sources.filter(
        (s): s is Form1099B =>
          s.kind === "form_1099b" && s.accountId === account.id && s.deletedAt === null && !excluded.has(s.id) && s.description.trim().toLowerCase() === description.trim().toLowerCase(),
      );
  const needsRelation = others.length > 0;
  const target = replaces ?? others[others.length - 1]?.id ?? null;
  const owner = account.ownerId === "joint" ? yf.people.find((p) => p.role === "primary")!.id : account.ownerId;
  const valid = amountOk(boxes.box4) && amountOk(boxes.box8, true) && amountOk(boxes.box9, true) && amountOk(boxes.box10, true) && amountOk(boxes.box11, true) && (!needsRelation || relation !== null);

  const save = () => {
    if (existing) {
      update((y) => reviseRecord(y, "sources", existing.id, { issuer, description, correctedAsPrinted: corrected, ...boxes } as Partial<Source>));
      return onDone();
    }
    if (relation === "same") return onDone();
    const id = newId();
    update((y) => {
      let next = addSource(y, {
        id,
        kind: "form_1099b",
        taxYear: imported?.cand.taxYear.found ? imported.cand.taxYear.value : y.taxYear,
        fingerprint: imported ? candidateFingerprint(imported.file.hash, imported.cand) : id,
        fileHash: imported?.file.hash ?? null,
        personId: owner,
        accountId: account.id,
        firmId: null,
        provenance: imported ? "imported" : "entered",
        addedAt: nowIso(),
        issuer,
        description,
        correctedAsPrinted: corrected || relation === "corrects",
        ...boxes,
      } as Parameters<typeof addSource>[1]).yearFile;
      if (relation === "corrects" && target) {
        const orig = next.sources.find((s) => s.id === target)!;
        next = addRecord(next, "corrections", { id: newId(), originalId: target, replacementId: id, status: "confirmed", confirmedRevisions: { original: orig.revision, replacement: 1 } });
      }
      if (relation === "separate") {
        const all = [...others.map((o) => ({ id: o.id, rev: o.revision })), { id, rev: 1 }];
        next = addRecord(next, "decisions", { id: newId(), kind: "confirm_both_info_returns", sourceIds: all.map((a) => a.id), revisions: all.map((a) => a.rev) });
      }
      return next;
    });
    onDone();
  };

  const set = (k: keyof typeof boxes) => (a: Amount) => setBoxes((b) => ({ ...b, [k]: a }));
  return (
    <Editor title={existing ? "Edit 1099-B" : `1099-B for ${account.label}`} onSave={save} onCancel={onDone} canSave={valid}>
      <p className="field-hint">Copy each box exactly as printed, including losses shown in parentheses. Leave a box empty if it's empty on the form.</p>
      {!existing && !importing && !imported && (
        <button type="button" className="btn btn--outline btn--sm" onClick={() => setImporting(true)}>
          Import from PDF
        </button>
      )}
      {importing && <ImportPanel want="form_1099b" onUse={useImport} onClose={() => setImporting(false)} />}
      {imported && <ImportedBanner file={imported.file} taxYear={imported.cand.taxYear} organizerYear={yf.taxYear} onClear={clearImport} />}
      <div key={fillKey}>
        <TextInput label="Payer (top left of the form)" value={issuer} onChange={setIssuer} placeholder="e.g. NinjaTrader Clearing, LLC" hint={hint("issuer")} />
        <TextInput
          label="Box 1a description"
          value={description}
          onChange={setDescription}
          hint={imported ? importHint(imported.cand.description, "copy the contract type from box 1a") : "Some brokers send a separate set of boxes 8–11 for each contract type."}
        />
        <div className="box-grid">
          <AmountInput label="Box 8" hint={hint("box8") ?? "Profit or (loss) realized in 2026 on closed contracts"} value={boxes.box8} onChange={set("box8")} emptyMeans="blank" allowNegative />
          <AmountInput label="Box 9" hint={hint("box9") ?? "Unrealized profit or (loss) on open contracts at Dec 31, 2025"} value={boxes.box9} onChange={set("box9")} emptyMeans="blank" allowNegative />
          <AmountInput label="Box 10" hint={hint("box10") ?? "Unrealized profit or (loss) on open contracts at Dec 31, 2026"} value={boxes.box10} onChange={set("box10")} emptyMeans="blank" allowNegative />
          <AmountInput label="Box 11" hint={hint("box11") ?? "Aggregate profit or (loss) on contracts"} value={boxes.box11} onChange={set("box11")} emptyMeans="blank" allowNegative />
          <AmountInput label="Box 4" hint={hint("box4") ?? "Federal income tax withheld (usually empty)"} value={boxes.box4} onChange={set("box4")} emptyMeans="blank" />
        </div>
        <label className="check">
          <input type="checkbox" checked={corrected} onChange={(e) => setCorrected(e.target.checked)} />
          The form has the CORRECTED box checked
        </label>
        {imported && <p className="field-hint">{importHint(imported.cand.corrected, "look for a check mark in the CORRECTED box at the top of the form")}</p>}
      </div>
      {needsRelation && (
        <div className="ask">
          <Chips
            legend={`${account.label} already has a 1099-B for ${description || "these contracts"}. How does this one relate to it?`}
            value={relation}
            options={[
              { value: "corrects", label: "It corrects the earlier one" },
              { value: "separate", label: "It's a separate form" },
              { value: "same", label: "It's the same form again" },
            ]}
            onChange={setRelation}
            hint="A corrected form replaces the earlier one; the two are never added together."
          />
          {relation === "corrects" && others.length > 1 && (
            <Chips legend="Which form does it replace?" value={target} options={others.map((o) => ({ value: o.id, label: `${o.issuer || "1099-B"} (box 11 ${o.box11.state === "value" ? fmtUsd(o.box11.value) : "not entered"})` }))} onChange={setReplaces} />
          )}
          {relation === "same" && <p className="field-hint">Nothing will be added.</p>}
        </div>
      )}
    </Editor>
  );
}

function StatementEditor({ account, existing, onDone }: { account: Account; existing: Statement | null; onDone: () => void }) {
  const { yf, update } = useOrganizer();
  const [from, setFrom] = useState<string | null>(existing?.periodFrom ?? `${yf.taxYear}-01-01`);
  const [to, setTo] = useState<string | null>(existing?.periodTo ?? `${yf.taxYear}-12-31`);
  const [realized, setRealized] = useState<Amount>(existing?.realizedPnl ?? UNKNOWN);
  const [prior, setPrior] = useState<Amount>(existing?.unrealizedPriorYearEnd ?? UNKNOWN);
  const [ye, setYe] = useState<Amount>(existing?.unrealizedYearEnd ?? UNKNOWN);
  const [fees, setFees] = useState<"yes" | "no" | null>(existing ? (existing.commissionsIncluded === null ? null : existing.commissionsIncluded ? "yes" : "no") : null);
  const owner = account.ownerId === "joint" ? yf.people.find((p) => p.role === "primary")!.id : account.ownerId;

  const save = () => {
    const fields = { periodFrom: from, periodTo: to, realizedPnl: realized, unrealizedPriorYearEnd: prior, unrealizedYearEnd: ye, commissionsIncluded: fees === null ? null : fees === "yes" };
    update((y) => {
      if (existing) {
        let next = reviseRecord(y, "sources", existing.id, fields as Partial<Source>);
        const rev = next.sources.find((s) => s.id === existing.id)!.revision;
        for (const d of next.decisions)
          if (d.kind === "section1256_basis" && d.sourceId === existing.id && d.deletedAt === null) next = reviseRecord(next, "decisions", d.id, { sourceRevision: rev } as never);
        return next;
      }
      const id = newId();
      let next = addSource(y, {
        id,
        kind: "broker_statement",
        taxYear: y.taxYear,
        fingerprint: id,
        fileHash: null,
        personId: owner,
        accountId: account.id,
        firmId: null,
        provenance: "entered",
        addedAt: nowIso(),
        ...fields,
      } as Parameters<typeof addSource>[1]).yearFile;
      next = addRecord(next, "decisions", { id: newId(), kind: "section1256_basis", accountId: account.id, basis: "statement", sourceId: id, sourceRevision: 1 });
      return next;
    });
    onDone();
  };

  return (
    <Editor title="Year-end statement" onSave={save} onCancel={onDone} canSave={amountOk(realized, true) && amountOk(prior, true) && amountOk(ye, true)}>
      <p className="field-hint">
        Only for an account that won't get a 1099-B. Use the broker's December statement or annual summary. The figure is open-position value at both
        year-ends plus realized profit or loss, after commissions.
      </p>
      <div className="box-grid">
        <DateInput label="Statement covers from" value={from} onChange={setFrom} />
        <DateInput label="to" value={to} onChange={setTo} />
        <AmountInput label="Realized profit or loss for 2026" value={realized} onChange={setRealized} emptyMeans="unknown" allowNegative />
        <AmountInput label="Open-position value at Dec 31, 2025" hint="Enter 0 if nothing was open." value={prior} onChange={setPrior} emptyMeans="unknown" allowNegative />
        <AmountInput label="Open-position value at Dec 31, 2026" hint="Enter 0 if nothing was open." value={ye} onChange={setYe} emptyMeans="unknown" allowNegative />
      </div>
      <YesNoField legend="Does the realized figure already include commissions and fees?" value={fees} onChange={setFees} />
    </Editor>
  );
}
